/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Media Asset Grid Card Component (Task 10.4.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import {
  Image as ImageIcon,
  Film,
  Volume2,
  Heart,
  Check,
  Play,
  Sparkles,
} from 'lucide-react';
import { MediaAsset, formatBytes } from '../../stores/useMediaGalleryStore';

interface MediaGridCardProps {
  asset: MediaAsset;
  isSelected: boolean;
  isSelectionMode: boolean;
  onSelect: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onInspect: (asset: MediaAsset) => void;
}

export const MediaGridCard: React.FC<MediaGridCardProps> = ({
  asset,
  isSelected,
  isSelectionMode,
  onSelect,
  onToggleFavorite,
  onInspect,
}) => {
  const isVideo = asset.assetType === 'video';
  const isAudio = asset.assetType === 'audio';
  const isImage = asset.assetType === 'image';

  return (
    <div
      onClick={() => {
        if (isSelectionMode) {
          onSelect(asset.id);
        } else {
          onInspect(asset);
        }
      }}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border transition-all duration-200 cursor-pointer bg-surface ${
        isSelected
          ? 'border-primary ring-2 ring-primary/40 shadow-glow-primary/20'
          : 'border-surface-border hover:border-primary/50 hover:shadow-glass'
      }`}
    >
      {/* 1. Media Visual Stage */}
      <div className="relative aspect-square w-full overflow-hidden bg-surface-elevated flex items-center justify-center">
        {isAudio ? (
          <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-gradient-to-br from-primary/10 via-surface-elevated to-accent/10">
            <div className="w-16 h-16 rounded-2xl bg-primary/15 text-primary flex items-center justify-center shadow-glass group-hover:scale-105 transition-transform duration-300">
              <Volume2 className="w-8 h-8" />
            </div>
            <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-foreground-muted">
              <span>{asset.durationSeconds ?? 30}s</span>
              <span>•</span>
              <span>{asset.bitrate ?? '320 kbps'}</span>
            </div>
          </div>
        ) : (
          <img
            src={asset.thumbnailUrl}
            alt={asset.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}

        {/* Video Play Overlay */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/25 opacity-75 group-hover:opacity-100 transition-opacity">
            <div className="w-11 h-11 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center shadow-lg border border-white/20 group-hover:scale-110 transition-transform">
              <Play className="w-5 h-5 ml-0.5 fill-current" />
            </div>
          </div>
        )}

        {/* Top-Left: Type Badge or Selection Checkbox */}
        <div className="absolute top-2.5 left-2.5 z-10 flex items-center gap-1.5">
          {isSelectionMode ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSelect(asset.id);
              }}
              className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors border ${
                isSelected
                  ? 'bg-primary border-primary text-primary-fg shadow-sm'
                  : 'bg-surface/80 backdrop-blur-md border-surface-border text-transparent hover:border-primary'
              }`}
            >
              <Check className="w-4 h-4 stroke-[3]" />
            </button>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-black/65 backdrop-blur-md text-white border border-white/15">
              {isImage && <ImageIcon className="w-3 h-3 text-cyan-400" />}
              {isVideo && <Film className="w-3 h-3 text-emerald-400" />}
              {isAudio && <Volume2 className="w-3 h-3 text-amber-400" />}
              <span className="capitalize">{asset.assetType}</span>
            </span>
          )}

          {asset.aspectRatio && !isSelectionMode && (
            <span className="hidden sm:inline-flex px-1.5 py-0.5 rounded text-[9px] font-mono bg-black/50 text-white/80 backdrop-blur-sm">
              {asset.aspectRatio}
            </span>
          )}
        </div>

        {/* Top-Right: Favorite Button */}
        <div className="absolute top-2.5 right-2.5 z-10">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(asset.id);
            }}
            className={`w-7 h-7 rounded-lg flex items-center justify-center backdrop-blur-md transition-all ${
              asset.isFavorite
                ? 'bg-rose-500/90 text-white shadow-sm'
                : 'bg-black/50 text-white/80 hover:bg-black/75 hover:text-white'
            }`}
          >
            <Heart className={`w-4 h-4 ${asset.isFavorite ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Bottom-Right: Duration / Resolution Badge */}
        <div className="absolute bottom-2.5 right-2.5 z-10">
          {isVideo && asset.durationSeconds && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-medium bg-black/70 backdrop-blur-md text-white border border-white/10">
              {asset.durationSeconds}s
            </span>
          )}
          {isImage && asset.width && asset.height && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-black/70 backdrop-blur-md text-white/90 border border-white/10">
              {asset.width}x{asset.height}
            </span>
          )}
        </div>
      </div>

      {/* 2. Metadata Footer */}
      <div className="p-3.5 flex flex-col justify-between flex-1 space-y-2">
        <div>
          <h4
            className="text-xs font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors"
            title={asset.title}
          >
            {asset.title}
          </h4>
          {asset.prompt && (
            <p className="text-[11px] text-foreground-muted line-clamp-1 mt-0.5" title={asset.prompt}>
              {asset.prompt}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-surface-border text-[10px]">
          <span className="inline-flex items-center gap-1 font-medium text-foreground-muted">
            <Sparkles className="w-3 h-3 text-primary flex-shrink-0" />
            <span className="truncate max-w-[110px]">{asset.generatorName}</span>
          </span>

          <div className="flex items-center gap-2 font-mono text-foreground-muted">
            <span>{formatBytes(asset.fileSizeBytes)}</span>
            <span>•</span>
            <span className="text-primary font-semibold">{asset.creditsCost} C</span>
          </div>
        </div>
      </div>
    </div>
  );
};
